import { createContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';
import { themesByMode } from '@config/theme.config';
import {
  normalizeThemeMode,
  THEME_TOGGLE_PAIR,
  themeModeIsDark,
  getAccentForMode,
} from '@config/theme.utils';
import { STORAGE_KEYS } from '@config/constants';
import { storage } from '@utils/storage';
import type { ThemeAccent, ThemeMode } from '@appTypes/common.types';

export interface ThemeContextValue {
  mode: ThemeMode;
  accent: ThemeAccent;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  isDark: boolean;
}

export const ThemeContext = createContext<ThemeContextValue>({
  mode: 'light',
  accent: 'blue',
  toggleTheme: () => {},
  setThemeMode: () => {},
  isDark: false,
});

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [mode, setMode] = useState<ThemeMode>(() =>
    normalizeThemeMode(storage.get(STORAGE_KEYS.THEME, 'light')),
  );

  const toggleTheme = useCallback(() => {
    setMode((prev) => {
      const next = THEME_TOGGLE_PAIR[prev];
      storage.set(STORAGE_KEYS.THEME, next);
      return next;
    });
  }, []);

  const setThemeMode = useCallback((newMode: ThemeMode) => {
    setMode(newMode);
    storage.set(STORAGE_KEYS.THEME, newMode);
  }, []);

  const isDark = themeModeIsDark(mode);
  const accent = getAccentForMode(mode);

  const themeConfig = useMemo(() => {
    const base = themesByMode[mode];
    return {
      ...base,
      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    };
  }, [mode, isDark]);

  const contextValue = useMemo(
    () => ({ mode, accent, toggleTheme, setThemeMode, isDark }),
    [mode, accent, toggleTheme, setThemeMode, isDark],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider theme={themeConfig}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
}
